import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface ConceptCard {
  name: string;
  kpi: string;
  desc: string;
  color: string;
}

interface TechCard {
  name: string;
  gain: string;
  desc: string;
  color: string;
}

interface TransportMode {
  name: string;
  icon: string;
  cost: number;
  speed: number;
}

interface KPIGauge {
  label: string;
  value: number;
  unit: string;
  max: number;
  color: string;
}

interface SupplyChainTemplateProps {
  topic: string;
  subtitle: string;
  concepts: ConceptCard[];
  technologies: TechCard[];
  transportModes: TransportMode[];
  gauges: KPIGauge[];
  chainNodes: { label: string; icon: string }[];
  stats: { value: string; label: string }[];
  bottomInsight: string;
}

const DEFAULT_PROPS: SupplyChainTemplateProps = {
  topic: '供应链管理与物流科学',
  subtitle: 'Supply Chain Management & Logistics',
  concepts: [
    { name: '采购管理', kpi: '成本降低15%', desc: '供应商评估·集中采购·价格谈判', color: '#3b82f6' },
    { name: '库存优化', kpi: '周转提升40%', desc: 'ABC分类·安全库存·EOQ模型', color: '#f97316' },
    { name: '物流配送', kpi: '时效提升30%', desc: '路径优化·多式联运·末端配送', color: '#22c55e' },
    { name: '需求预测', kpi: '准确率92%', desc: 'AI算法·历史数据·市场因子分析', color: '#8b5cf6' },
  ],
  technologies: [
    { name: 'WMS仓储系统', gain: '效率+60%', desc: '智能分拣·货位优化·自动补货', color: '#f97316' },
    { name: 'TMS运输管理', gain: '成本-25%', desc: '路径规划·运力调度·运费结算', color: '#3b82f6' },
    { name: 'IoT追踪', gain: '可视度99%', desc: '实时定位·温湿监控·异常预警', color: '#22c55e' },
    { name: 'AI预测', gain: '精度+35%', desc: '需求感知·风险预判·动态定价', color: '#8b5cf6' },
  ],
  transportModes: [
    { name: '公路', icon: '🚛', cost: 45, speed: 70 },
    { name: '铁路', icon: '🚂', cost: 30, speed: 55 },
    { name: '海运', icon: '🚢', cost: 15, speed: 20 },
    { name: '空运', icon: '✈️', cost: 90, speed: 98 },
  ],
  gauges: [
    { label: '库存周转', value: 12, unit: '次/年', max: 20, color: '#f97316' },
    { label: '准时交付率', value: 96, unit: '%', max: 100, color: '#22c55e' },
    { label: '订单满足率', value: 98, unit: '%', max: 100, color: '#3b82f6' },
    { label: '物流成本占比', value: 8, unit: '%', max: 30, color: '#8b5cf6' },
  ],
  chainNodes: [
    { label: '原材料', icon: '⛏️' },
    { label: '工厂', icon: '🏭' },
    { label: '仓库', icon: '🏬' },
    { label: '配送中心', icon: '📦' },
    { label: '零售', icon: '🏪' },
    { label: '消费者', icon: '🧑' },
  ],
  stats: [
    { value: '$10万亿', label: '全球物流市场' },
    { value: '500万单/日', label: '亚马逊日处理' },
    { value: '3亿件/日', label: '中国快递日均' },
  ],
  bottomInsight: '供应链的本质不是物流——是信息流驱动的价值链协同',
};

export const SupplyChainTemplate: React.FC<Partial<SupplyChainTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const conceptStart = 50;
  const techStart = 50;
  const transportStart = 120;
  const gaugeStart = 200;
  const insightStart = 280;

  const insightOpacity = interpolate(frame, [insightStart, insightStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const dotOffset = (frame % 60) / 60;

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(140deg, #0a0a14 0%, #141a1e 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Grid background */}
      <svg style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }} width="1920" height="1080">
        {Array.from({ length: 24 }).map((_, i) => (
          <line key={`vl-${i}`} x1={i * 84} y1={0} x2={i * 84} y2={1080} stroke="#3b82f6" strokeWidth="0.5" opacity="0.04" />
        ))}
        {Array.from({ length: 13 }).map((_, i) => (
          <line key={`hl-${i}`} x1={0} y1={i * 90} x2={1920} y2={i * 90} stroke="#3b82f6" strokeWidth="0.5" opacity="0.04" />
        ))}
      </svg>

      {/* HEADER */}
      <div
        style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 112,
          background: 'linear-gradient(180deg, rgba(10,10,20,0.98) 60%, transparent 100%)',
          display: 'flex', alignItems: 'center',
          padding: '0 48px',
          justifyContent: 'space-between',
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * -18}px)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '8px 22px', background: '#0f1f40',
            borderRadius: 10, fontSize: 20, fontWeight: 700,
            color: '#3b82f6', border: '1px solid #3b82f640',
            whiteSpace: 'nowrap',
          }}>
            📦 SUPPLY CHAIN
          </div>
          <h1 style={{ fontSize: 40, fontWeight: 900, margin: 0, color: '#f8fafc', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
            {p.topic}
          </h1>
          <p style={{ fontSize: 24, color: '#475569', margin: 0, whiteSpace: 'nowrap' }}>{p.subtitle}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 20, color: '#475569', marginBottom: 2 }}>全球物流规模</div>
          <div style={{ fontSize: 40, fontWeight: 900, color: '#3b82f6', lineHeight: 1 }}>
            {p.stats[0]?.value ?? '$10万亿'}
          </div>
        </div>
      </div>

      {/* Header separator */}
      <div style={{
        position: 'absolute', top: 110, left: 48, right: 48, height: 1,
        background: 'linear-gradient(90deg, transparent, #3b82f650, transparent)',
        opacity: headerIn,
      }} />

      {/* SUPPLY CHAIN FLOW */}
      <svg
        style={{ position: 'absolute', left: 0, top: 114 }}
        width="1920"
        height="144"
        viewBox="0 0 1920 144"
      >
        <defs>
          <marker id="flowArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0,0 10,3.5 0,7" fill="#3b82f6" />
          </marker>
        </defs>
        {p.chainNodes.map((node, i) => {
          const nx = 160 + i * 320;
          const ny = 62;
          const nodeOpacity = interpolate(frame, [20 + i * 12, 40 + i * 12], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          const nodeColors = ['#64748b', '#f97316', '#f97316', '#3b82f6', '#22c55e', '#8b5cf6'];
          return (
            <g key={`node-${i}`} opacity={nodeOpacity}>
              <circle cx={nx} cy={ny} r="43" fill={`${nodeColors[i]}20`} stroke={nodeColors[i]} strokeWidth="2.5" />
              <text x={nx} y={ny + 9} textAnchor="middle" fontSize="28">{node.icon}</text>
              <text x={nx} y={ny + 69} textAnchor="middle" fill="#94a3b8" fontSize="20" fontWeight="700">{node.label}</text>
            </g>
          );
        })}
        {Array.from({ length: 5 }).map((_, i) => {
          const x1 = 204 + i * 320;
          const x2 = 436 + i * 320;
          const y = 62;
          const lineOpacity = interpolate(frame, [30 + i * 12, 50 + i * 12], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          const dotX = x1 + (x2 - x1) * dotOffset;
          return (
            <g key={`arrow-${i}`} opacity={lineOpacity}>
              <line x1={x1} y1={y} x2={x2} y2={y} stroke="#3b82f650" strokeWidth="2.5" markerEnd="url(#flowArrow)" />
              <circle cx={dotX} cy={y} r="5" fill="#3b82f6" opacity="0.9" />
            </g>
          );
        })}
      </svg>

      {/* LEFT PANEL: Core Modules */}
      <div style={{ position: 'absolute', top: 300, left: 40, width: 340 }}>
        <div style={{
          fontSize: 26, color: '#64748b', marginBottom: 14,
          letterSpacing: '0.08em', fontWeight: 700,
          paddingBottom: 8, borderBottom: '1px solid #1e293b',
        }}>
          核心模块
        </div>
        {p.concepts.map((c, i) => {
          const s = spring({ frame: frame - (conceptStart + i * 16), fps, config: { damping: 14, stiffness: 120 } });
          return (
            <div
              key={i}
              style={{
                background: '#0d0d1e',
                borderRadius: 12,
                padding: '14px 16px',
                marginBottom: 12,
                border: `1px solid ${c.color}30`,
                borderLeft: `4px solid ${c.color}`,
                opacity: s,
                transform: `translateX(${(1 - s) * -30}px)`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 23, fontWeight: 800, color: c.color }}>{c.name}</span>
                <span style={{ fontSize: 20, color: c.color, background: `${c.color}20`, padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>{c.kpi}</span>
              </div>
              <div style={{ fontSize: 20, color: '#94a3b8', lineHeight: 1.6 }}>{c.desc}</div>
            </div>
          );
        })}
      </div>

      {/* CENTER: Transport Modes */}
      <div style={{
        position: 'absolute', left: 404, top: 280, width: 1112,
        background: 'rgba(13,13,30,0.5)',
        borderRadius: 16, padding: '20px 32px',
        border: '1px solid #1e293b50',
      }}>
        <div style={{
          fontSize: 26, color: '#94a3b8', marginBottom: 20, fontWeight: 700,
          paddingBottom: 10, borderBottom: '1px solid #1e293b',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>运输方式对比 · Transport Comparison</span>
          <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, background: '#ef4444', borderRadius: 3 }} />
              <span style={{ fontSize: 20, color: '#64748b' }}>成本指数</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, background: '#22c55e', borderRadius: 3 }} />
              <span style={{ fontSize: 20, color: '#64748b' }}>速度指数</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center' }}>
          {p.transportModes.map((tm, i) => {
            const s = spring({ frame: frame - (transportStart + i * 14), fps, config: { damping: 14, stiffness: 110 } });
            const maxBar = 160;
            const costBar = interpolate(
              frame,
              [transportStart + i * 14, transportStart + i * 14 + 40],
              [0, (tm.cost / 100) * maxBar],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            const speedBar = interpolate(
              frame,
              [transportStart + i * 14 + 5, transportStart + i * 14 + 45],
              [0, (tm.speed / 100) * maxBar],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            return (
              <div key={i} style={{
                textAlign: 'center', opacity: s,
                transform: `translateY(${(1 - s) * 20}px)`,
                flex: 1, maxWidth: 220,
              }}>
                <div style={{ fontSize: 44 }}>{tm.icon}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#e2e8f0', marginTop: 8 }}>{tm.name}</div>
                <div style={{ marginTop: 14, position: 'relative', height: maxBar }}>
                  <div style={{
                    position: 'absolute', left: '16%', bottom: 0, width: '28%',
                    height: `${costBar}px`,
                    background: 'linear-gradient(180deg, #ef4444, #ef444460)',
                    borderRadius: '4px 4px 0 0',
                  }} />
                  <div style={{
                    position: 'absolute', right: '16%', bottom: 0, width: '28%',
                    height: `${speedBar}px`,
                    background: 'linear-gradient(180deg, #22c55e, #22c55e60)',
                    borderRadius: '4px 4px 0 0',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 8 }}>
                  <span style={{ fontSize: 15, color: '#ef4444', fontWeight: 700, marginLeft: 20 }}>{tm.cost}</span>
                  <span style={{ fontSize: 15, color: '#22c55e', fontWeight: 700, marginRight: 15 }}>{tm.speed}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT PANEL: Technologies */}
      <div style={{ position: 'absolute', top: 300, right: 40, width: 340 }}>
        <div style={{
          fontSize: 26, color: '#64748b', marginBottom: 14,
          letterSpacing: '0.08em', fontWeight: 700,
          paddingBottom: 8, borderBottom: '1px solid #1e293b',
        }}>
          数字化技术
        </div>
        {p.technologies.map((t, i) => {
          const s = spring({ frame: frame - (techStart + i * 16), fps, config: { damping: 14, stiffness: 120 } });
          return (
            <div
              key={i}
              style={{
                background: '#0d0d1e',
                borderRadius: 12,
                padding: '14px 16px',
                marginBottom: 12,
                border: `1px solid ${t.color}30`,
                borderRight: `4px solid ${t.color}`,
                opacity: s,
                transform: `translateX(${(1 - s) * 30}px)`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: t.color }}>{t.name}</span>
                <span style={{ fontSize: 20, color: '#22c55e', background: '#22c55e20', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>{t.gain}</span>
              </div>
              <div style={{ fontSize: 20, color: '#94a3b8', lineHeight: 1.6 }}>{t.desc}</div>
            </div>
          );
        })}
      </div>

      {/* KPI GAUGES */}
      <div style={{ position: 'absolute', bottom: 90, left: 44, right: 44, display: 'flex', gap: 20 }}>
        {p.gauges.map((g, i) => {
          const gs = spring({ frame: frame - (gaugeStart + i * 14), fps, config: { damping: 14, stiffness: 100 } });
          const totalArcDeg = 240;
          const valueFraction = g.value / g.max;
          const fillDeg = interpolate(
            frame,
            [gaugeStart + i * 14, gaugeStart + i * 14 + 55],
            [0, valueFraction * totalArcDeg],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );

          const gCx = 70, gCy = 58, gR = 44;
          const startRad = (210 * Math.PI) / 180;
          const endRad = (330 * Math.PI) / 180;
          const bgSx = gCx + gR * Math.cos(startRad);
          const bgSy = gCy - gR * Math.sin(startRad);
          const bgEx = gCx + gR * Math.cos(endRad);
          const bgEy = gCy - gR * Math.sin(endRad);

          const fillEndRad = ((210 - fillDeg) * Math.PI) / 180;
          const fEx = gCx + gR * Math.cos(fillEndRad);
          const fEy = gCy - gR * Math.sin(fillEndRad);
          const fLarge = fillDeg > 180 ? 1 : 0;

          const valStr = String(g.value);
          return (
            <div
              key={i}
              style={{
                flex: 1, background: '#0d0d1e',
                borderRadius: 12, padding: '14px 12px',
                textAlign: 'center', border: `1px solid ${g.color}30`,
                opacity: gs, transform: `scale(${0.85 + 0.15 * gs})`,
              }}
            >
              <svg width="140" height="96" viewBox="0 0 140 96">
                <path
                  d={`M${bgSx.toFixed(1)},${bgSy.toFixed(1)} A${gR},${gR} 0 1,1 ${bgEx.toFixed(1)},${bgEy.toFixed(1)}`}
                  fill="none" stroke="#1e293b" strokeWidth="10" strokeLinecap="round"
                />
                {fillDeg > 0.5 && (
                  <path
                    d={`M${bgSx.toFixed(1)},${bgSy.toFixed(1)} A${gR},${gR} 0 ${fLarge},1 ${fEx.toFixed(1)},${fEy.toFixed(1)}`}
                    fill="none" stroke={g.color} strokeWidth="10" strokeLinecap="round"
                  />
                )}
                <text x={gCx} y={gCy + 14} textAnchor="middle" fontSize="28" fontWeight="900">
                  <tspan fill={g.color}>{g.value}</tspan>
                  <tspan fill="#94a3b8" fontSize="16" fontWeight="600">{g.unit}</tspan>
                </text>
              </svg>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0', marginTop: 2 }}>{g.label}</div>
            </div>
          );
        })}
      </div>

      {/* BOTTOM INSIGHT */}
      <div
        style={{
          position: 'absolute',
          bottom: 6,
          left: 44,
          right: 44,
          background: 'rgba(10,15,25,0.92)',
          borderRadius: 14,
          padding: '14px 26px',
          border: '1px solid #3b82f625',
          borderTop: '3px solid #3b82f6',
          opacity: insightOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 28 }}>📦</span>
          <span style={{ fontSize: 22, color: '#e2e8f0', lineHeight: 1.5 }}>{p.bottomInsight}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, durationInFrames], [0, 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #3b82f6, #f97316, #22c55e, #8b5cf6)',
        }}
      />
    </AbsoluteFill>
  );
};
